import { useAllAnimalExhibits } from "hooks/use-all-animal-exhibit-data";
import { getUniqueObjects } from "helpers/data/get-unique-objects";
import { AnimalExhibitFilter } from "types/filters";

export const getAnimalExhibitsForSignatureEventLocations = (
    signatureEventLocations: { id: string | undefined }[]
): Queries.AnimalExhibitFieldsFragment[] => {
    const animalExhibits = useAllAnimalExhibits();

    return animalExhibits.filter((animalExhibit) => {
        return animalExhibit.signatureEventDetails?.some((detail) =>
            signatureEventLocations.some(
                (location) => location.id === detail?.id
            )
        );
    });
};

export const getAnimalExhibitsForSignatureEventUniversalTag = (
    signatureEventId: string,
    universalTagId: string
): Queries.AnimalExhibitFieldsFragment[] => {
    const animalExhibits = useAllAnimalExhibits();

    return animalExhibits.filter((animalExhibit) => {
        return animalExhibit.signatureEventDetails?.some(
            (detail) =>
                detail?.signatureEvent?.id === signatureEventId &&
                animalExhibit.universalTags?.some(
                    (tag) => tag?.id === universalTagId
                )
        );
    });
};

export const getAnimalExhibitsForBuilding = (
    buildingId: string
): Queries.AnimalExhibitFieldsFragment[] => {
    const exhibits = useAllAnimalExhibits();

    return exhibits.filter((exhibit) => {
        return exhibit.signatureEventDetails?.some((detail) => {
            return detail?.locations?.some((location) => {
                const isTargetBuilding =
                    location?.__typename === "DatoCmsBuilding" &&
                    location.id === buildingId;
                const isLocationInBuilding =
                    location?.__typename === "DatoCmsLocation" &&
                    location.building?.id === buildingId;

                return isTargetBuilding || isLocationInBuilding;
            });
        });
    });
};

export const getAnimalExhibitsForFilters = (
    filters: AnimalExhibitFilter[],
    signatureEventId?: string
): Queries.AnimalExhibitFieldsFragment[] => {
    // if there are no filters and no signature event, return all animal exhibits
    if (filters.length === 0 && !signatureEventId) {
        return useAllAnimalExhibits();
    }

    // if there are no filters, return all animal exhibits for signature event
    if (filters.length === 0) {
        return useAllAnimalExhibits().filter((exhibit) =>
            exhibit.signatureEventDetails?.some(
                (detail) => detail?.signatureEvent?.id === signatureEventId
            )
        );
    }

    const exhibitsForUniversalTags = getAnimalExhibitsForUniveralTags(
        filters,
        signatureEventId
    );

    const animalExhibitsForAnimals = getAnimalExhibitsForAnimals(
        filters,
        signatureEventId
    );

    const animalExhibitsForCosts = getAnimalExhibitsForCosts(
        filters,
        signatureEventId
    );

    const animalExhibitsForAgeGroups = getAnimalExhibitsForAgeGroups(
        filters,
        signatureEventId
    );

    // only get the records that are in all of the arrays using the id.
    const commonExhibits = getCommonAnimalExhibits(
        exhibitsForUniversalTags,
        animalExhibitsForAnimals,
        animalExhibitsForCosts,
        animalExhibitsForAgeGroups
    );

    return commonExhibits;
};

const getAnimalExhibitsForUniveralTags = (
    filters: AnimalExhibitFilter[],
    signatureEventId?: string
): Queries.AnimalExhibitFieldsFragment[] => {
    const exhibits = useAllAnimalExhibits();

    // get filters where __typename = DatoCmsUniversalTag
    const universalTagFilters = filters.filter(
        (filter) => filter.__typename === "DatoCmsUniversalTag"
    );

    if (universalTagFilters.length === 0) {
        return [];
    }

    return exhibits.filter((exhibit) => {
        // Check for signatureEventId and matching details
        if (
            signatureEventId &&
            !exhibit.signatureEventDetails?.some(
                (detail) => detail?.signatureEvent?.id === signatureEventId
            )
        ) {
            return false; // No matches found, exclude this exhibit
        }

        // Check for matching universal tag filters
        return exhibit.universalTags?.some((tag) =>
            universalTagFilters.some((filter) => filter.id === tag?.id)
        );
    });
};

const getAnimalExhibitsForAnimals = (
    filters: AnimalExhibitFilter[],
    signatureEventId?: string
): Queries.AnimalExhibitFieldsFragment[] => {
    const exhibits = useAllAnimalExhibits();

    // get filters where __typename = DatoCmsAnimal
    const animalFilters = filters.filter(
        (filter) => filter.__typename === "DatoCmsAnimal"
    );

    if (animalFilters.length === 0) {
        return [];
    }

    return exhibits.filter((exhibit) => {
        // Check for signatureEventId and matching details
        if (
            signatureEventId &&
            !exhibit.signatureEventDetails?.some(
                (detail) => detail?.signatureEvent?.id === signatureEventId
            )
        ) {
            return false; // No matches found, exclude this exhibit
        }

        // Check for matching animal filters
        return exhibit.animals?.some((animal) =>
            animalFilters.some((filter) => filter.id === animal?.id)
        );
    });
};

const getAnimalExhibitsForCosts = (
    filters: AnimalExhibitFilter[],
    signatureEventId?: string
): Queries.AnimalExhibitFieldsFragment[] => {
    const exhibits = useAllAnimalExhibits();

    // get filters where __typename = DatoCmsCost
    const costFilters = filters.filter(
        (filter) => filter.__typename === "DatoCmsCost"
    );

    if (costFilters.length === 0) {
        return [];
    }

    return exhibits.filter((exhibit) => {
        // Check for signatureEventId and matching details
        if (
            signatureEventId &&
            !exhibit.signatureEventDetails?.some(
                (detail) => detail?.signatureEvent?.id === signatureEventId
            )
        ) {
            return false; // No matches found, exclude this exhibit
        }

        // Check for matching cost filters
        return costFilters.some((filter) => filter.id === exhibit.cost?.id);
    });
};

const getAnimalExhibitsForAgeGroups = (
    filters: AnimalExhibitFilter[],
    signatureEventId?: string
): Queries.AnimalExhibitFieldsFragment[] => {
    const exhibits = useAllAnimalExhibits();

    // get filters where __typename = DatoCmsAgeGroup
    const ageGroupFilters = filters.filter(
        (filter) => filter.__typename === "DatoCmsAgeGroup"
    );

    if (ageGroupFilters.length === 0) {
        return [];
    }

    return exhibits.filter((exhibit) => {
        // Check for signatureEventId and matching details
        if (
            signatureEventId &&
            !exhibit.signatureEventDetails?.some(
                (detail) => detail?.signatureEvent?.id === signatureEventId
            )
        ) {
            return false; // No matches found, exclude this exhibit
        }

        return exhibit.recommendedAgeGroups?.some((ageGroup) =>
            ageGroupFilters.some((filter) => filter.id === ageGroup?.id)
        );
    });
};

function getCommonAnimalExhibits(
    ...exhibitArrays: Queries.AnimalExhibitFieldsFragment[][]
): Queries.AnimalExhibitFieldsFragment[] {
    // Filter out empty arrays
    const nonEmptyExhibitArrays = exhibitArrays.filter(
        (array) => array.length > 0
    );
    
    if (nonEmptyExhibitArrays.length === 0) return [];

    // Extract ids from each array
    const idSets = nonEmptyExhibitArrays.map(
        (array) => new Set(array.map((exhibit) => exhibit.id))
    );

    // Find common ids
    const commonIds = [...idSets[0]].filter((id) =>
        idSets.every((set) => set.has(id))
    );

    // Filter exhibits to only include those with common ids
    const commonExhibits = nonEmptyExhibitArrays[0].filter((exhibit) =>
        commonIds.includes(exhibit.id)
    );

    // Remove any duplicate exhibits which existed in multiple input arrays
    return getUniqueObjects(commonExhibits);
}